import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { isAxiosError } from 'src/api/instance';
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import { selectors as incomeVerificationSelectors } from 'src/features/incomeVerification';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { useAppDispatch } from 'src/store';
import {
  DisputeCalculationResponse,
  IncomeVerification
} from 'src/ts/interfaces/incomeVerification';
import api, { CalculationNote, PreviousNote } from './api';
import { CalculationDocument, MISMATCH_CODES, OVERRIDE_APPLIED_CODE } from './constants';
import {
  disputeCalculationThunk,
  getPreviousDocumentsForAppeal,
  sendReportThunk
} from './services';
import { ManualOverrideRequest } from './ts/interfaces';

export const useSendReport = () => {
  const dispatch = useAppDispatch();
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  const onSendReport = useCallback(
    async (entryId: string, documents: IncomeVerification[]) => {
      try {
        await dispatch(sendReportThunk({ entryId, documents })).unwrap();

        showSnackbar(VariantType.success, 'The report has been sent', SnackTypes.none);
        return true;
      } catch (error) {
        showSnackbar(
          VariantType.error,
          'An error has occurred, please refresh and try again',
          SnackTypes.none
        );
        return false;
      }
    },
    [SnackTypes.none, VariantType.error, VariantType.success, dispatch, showSnackbar]
  );

  return {
    onSendReport
  };
};

export const useCalculationDispute = (folderId?: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const isDisputeLoading = useSelector(incomeVerificationSelectors.isDisputeLoading);
  const { isAdmin } = useUserRoles();

  const onDisputeCalculation = useCallback(
    async (entryId?: string, existingReviewId?: string) => {
      if (!entryId) {
        showSnackbar(VariantType.error, 'Unable to dispute. Entry id is missing', SnackTypes.none);

        return;
      }

      // Admin: skip appeal creation, navigate to calculation dispute route
      if (isAdmin) {
        navigate(`/my-work/calculation-dispute/${entryId}?redirectPath=/folder/${folderId}`);
        return;
      }

      try {
        const { data: disputeData } = (await dispatch(
          disputeCalculationThunk(entryId)
        ).unwrap()) as unknown as DisputeCalculationResponse;

        navigate(
          `/my-work/incomeVerification/${disputeData.income_verification_review_id}/${disputeData.entry_id}?redirectPath=/folder/${folderId}`
        );

        showSnackbar(VariantType.success, 'Income Calculation has been disputed', SnackTypes.none);
      } catch (error) {
        showSnackbar(
          VariantType.error,
          'An error has occurred, please refresh and try again',
          SnackTypes.none
        );
      }
    },
    [
      SnackTypes.none,
      VariantType.error,
      VariantType.success,
      dispatch,
      showSnackbar,
      navigate,
      folderId,
      isAdmin
    ]
  );

  return {
    isDisputeLoading,
    onDisputeCalculation
  };
};

/**
 * Hook for retrieving the previous income verification documents for an appeal/dispute
 */
export const useDisputeDocuments = ({
  shouldFetchDocuments = true,
  entryId
}: {
  shouldFetchDocuments?: boolean;
  entryId: string;
}): [disputeDocuments: IncomeVerification[] | undefined, isLoadingDisputeDocuments: boolean] => {
  const [isLoadingAppeal, setAppealLoading] = useState(true);
  const [disputeDocuments, setDisputeDocuments] = useState<IncomeVerification[] | undefined>();

  useEffect(() => {
    const getDocs = async () => {
      setAppealLoading(true);
      const result = await getPreviousDocumentsForAppeal(entryId);
      setDisputeDocuments(result);

      setAppealLoading(false);
    };
    if (shouldFetchDocuments) {
      getDocs();
    } else {
      setAppealLoading(false);
    }
  }, [entryId, shouldFetchDocuments]);

  return [disputeDocuments, isLoadingAppeal];
};

export const useManualOverride = () => {
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const [isOverrideLoading, setIsOverrideLoading] = useState(false);

  const onManualOverride = useCallback(
    async (entryId: string | undefined, payload: ManualOverrideRequest) => {
      if (!entryId) {
        showSnackbar(
          VariantType.error,
          'Unable to submit override. Entry id is missing',
          SnackTypes.none
        );
        return false;
      }

      try {
        setIsOverrideLoading(true);
        await api.overrideIncomeVerification(entryId, payload);
        showSnackbar(VariantType.success, 'Override report sent', SnackTypes.none);
        return true;
      } catch (error) {
        const detail =
          isAxiosError(error) && error.response?.status === 422
            ? error.response.data?.errors?.detail
            : undefined;
        showSnackbar(
          VariantType.error,
          typeof detail === 'string'
            ? detail
            : 'An error has occurred, please refresh and try again',
          SnackTypes.none
        );
        return false;
      } finally {
        setIsOverrideLoading(false);
      }
    },
    [SnackTypes.none, VariantType.error, VariantType.success, showSnackbar]
  );

  return { isOverrideLoading, onManualOverride };
};

export const useCompleteSpecialtyWithoutIncome = () => {
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const [isCompleting, setIsCompleting] = useState(false);

  const onCompleteWithoutIncome = useCallback(
    async (entryId: string | undefined, reasonCode: number) => {
      if (!entryId) {
        showSnackbar(VariantType.error, 'Unable to complete. Entry id is missing', SnackTypes.none);
        return false;
      }

      try {
        setIsCompleting(true);
        await api.completeSpecialtyWithoutIncome(entryId, reasonCode);
        showSnackbar(VariantType.success, 'Report sent', SnackTypes.none);
        return true;
      } catch (error) {
        showSnackbar(
          VariantType.error,
          'An error has occurred, please refresh and try again',
          SnackTypes.none
        );
        return false;
      } finally {
        setIsCompleting(false);
      }
    },
    [SnackTypes.none, VariantType.error, VariantType.success, showSnackbar]
  );

  return { isCompleting, onCompleteWithoutIncome };
};

export const useCalculationOverride = () => {
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const [isOverrideLoading, setIsOverrideLoading] = useState(false);

  const onCalculationOverride = useCallback(
    async (entryId: string | undefined, calculationId: string | undefined, note?: string) => {
      if (!entryId || !calculationId) {
        showSnackbar(
          VariantType.error,
          'Unable to submit override. Required data is missing',
          SnackTypes.none
        );
        return false;
      }

      try {
        setIsOverrideLoading(true);
        await api.overrideCalculation(entryId, calculationId, note);
        showSnackbar(VariantType.success, 'Calculation override saved', SnackTypes.none);
        return true;
      } catch (error) {
        const detail =
          isAxiosError(error) && error.response?.status === 422
            ? error.response.data?.errors?.detail
            : undefined;
        showSnackbar(
          VariantType.error,
          typeof detail === 'string'
            ? detail
            : 'An error has occurred, please refresh and try again',
          SnackTypes.none
        );
        return false;
      } finally {
        setIsOverrideLoading(false);
      }
    },
    [SnackTypes.none, VariantType.error, VariantType.success, showSnackbar]
  );

  return { isOverrideLoading, onCalculationOverride };
};

export const useCalculationNotes = ({
  entryId,
  enabled
}: {
  entryId: string | undefined;
  enabled: boolean;
}) => {
  const [calculationNotes, setCalculationNotes] = useState<CalculationNote[]>([]);

  useEffect(() => {
    if (!entryId || !enabled) return;
    api
      .getCalculationNotes(entryId)
      .then((response) => setCalculationNotes(response.data ?? []))
      .catch(() => setCalculationNotes([]));
  }, [entryId, enabled]);

  return { calculationNotes };
};

const OVERRIDE_SELECTIONS_KEY = 'income-verification-override-selections-';

export const useCalculationDocuments = ({
  entryId,
  enabled,
  applicantName
}: {
  entryId: string | undefined;
  enabled: boolean;
  applicantName?: string;
}) => {
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const [calculationId, setCalculationId] = useState<string | undefined>();
  const [previousNote, setPreviousNote] = useState<PreviousNote | null | undefined>();
  const [noteRequired, setNoteRequired] = useState(false);
  const [calculationDocuments, setCalculationDocuments] = useState<CalculationDocument[]>([]);
  const [documentErrorsMap, setDocumentErrorsMap] = useState<Record<string, number[]>>({});
  const [mismatchNamesMap, setMismatchNamesMap] = useState<
    Record<string, { applicant: string; document: string }>
  >({});
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [overrideSelections, setOverrideSelectionsRaw] = useState<Record<string, boolean>>({});

  const setOverrideSelections = useCallback(
    (
      update: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)
    ) => {
      setOverrideSelectionsRaw((prev) => {
        const next = typeof update === 'function' ? update(prev) : update;
        if (entryId) {
          window.sessionStorage.setItem(
            `${OVERRIDE_SELECTIONS_KEY}${entryId}`,
            JSON.stringify(next)
          );
        }
        return next;
      });
    },
    [entryId]
  );

  useEffect(() => {
    if (entryId && enabled) {
      setIsLoadingDocs(true);
      api
        .getCalculationDocuments(entryId)
        .then((response) => {
          // Normalize FVS response fields to match CalculationDocument shape.
          // FVS returns document_source_type (not source_type),
          // reason_codes [{code}] (not document_errors [number]),
          // and per-doc applicant_name is the name ON the document (mock calls
          // this recipient_name). When recipient_name is already set (mock data),
          // leave both fields as-is.
          // Use || (not ??) for applicantName so empty string falls through.
          const docs: CalculationDocument[] = (response.data?.documents ?? []).map(
            (raw: CalculationDocument) => ({
              ...raw,
              source_type: raw.source_type ?? raw.document_source_type,
              document_errors: raw.document_errors ?? raw.reason_codes?.map((r) => r.code),
              recipient_name: raw.recipient_name ?? raw.applicant_name,
              applicant_name: raw.recipient_name
                ? raw.applicant_name
                : applicantName || raw.applicant_name
            })
          );
          setCalculationId(response.data?.calculation_id ?? undefined);
          setPreviousNote(response.data?.previous_note ?? null);
          setNoteRequired(response.data?.note_required ?? false);
          setCalculationDocuments(docs);
          const errorsMap: Record<string, number[]> = {};
          const initialSelections: Record<string, boolean> = {};
          const namesMap: Record<string, { applicant: string; document: string }> = {};
          docs.forEach((doc) => {
            if (doc.document_errors?.length) {
              errorsMap[doc.document_id] = doc.document_errors;
            }
            const errors = doc.document_errors ?? [];
            const hasMismatch = errors.some((c) => MISMATCH_CODES.includes(c));
            const wasOverridden = errors.includes(OVERRIDE_APPLIED_CODE);
            if (hasMismatch || wasOverridden) {
              initialSelections[doc.document_id] = wasOverridden;
            }
            if (doc.applicant_name && doc.recipient_name) {
              namesMap[doc.document_id] = {
                applicant: doc.applicant_name,
                document: doc.recipient_name
              };
            }
          });
          setDocumentErrorsMap(errorsMap);

          // Restore saved selections, falling back to defaults for new keys
          const saved = window.sessionStorage.getItem(`${OVERRIDE_SELECTIONS_KEY}${entryId}`);
          if (saved) {
            let parsed: Record<string, boolean> = {};
            try {
              parsed = JSON.parse(saved);
            } catch {
              // Corrupt sessionStorage data -- fall back to defaults
            }
            const merged = { ...initialSelections };
            for (const key of Object.keys(merged)) {
              if (key in parsed) {
                merged[key] = parsed[key];
              }
            }
            setOverrideSelectionsRaw(merged);
          } else {
            setOverrideSelectionsRaw(initialSelections);
          }

          setMismatchNamesMap(namesMap);
        })
        .catch((err) => {
          console.error('Failed to load document data', err);
          showSnackbar(VariantType.error, 'Failed to load document data', SnackTypes.none);
        })
        .finally(() => setIsLoadingDocs(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, enabled, applicantName]);

  return {
    isLoadingDocs,
    calculationId,
    previousNote,
    noteRequired,
    calculationDocuments,
    documentErrorsMap,
    mismatchNamesMap,
    overrideSelections,
    setOverrideSelections
  };
};
